import { baseApi } from '@/services/api';
import { Project, Agent, AgentTask, CodeGeneration } from './projectsTypes';

export const projectsAPI = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Projects
    getProjects: builder.query<Project[], { status?: string }>({
      query: (params) => ({
        url: '/projects',
        method: 'GET',
        params,
      }),
      providesTags: ['Projects'],
    }),

    getProject: builder.query<Project, string>({
      query: (id) => ({
        url: `/projects/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'Projects', id }],
    }),

    createProject: builder.mutation<Project, Partial<Project>>({
      query: (project) => ({
        url: '/projects',
        method: 'POST',
        data: project,
      }),
      invalidatesTags: ['Projects'],
    }),

    updateProject: builder.mutation<Project, { id: string; data: Partial<Project> }>({
      query: ({ id, data }) => ({
        url: `/projects/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Projects', id }, 'Projects'],
    }),

    deleteProject: builder.mutation<void, string>({
      query: (id) => ({
        url: `/projects/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Projects'],
    }),

    // AI Agents
    getAgents: builder.query<Agent[], void>({
      query: () => ({
        url: '/agents',
        method: 'GET',
      }),
      providesTags: ['Agents'],
    }),

    getAgent: builder.query<Agent, string>({
      query: (id) => ({
        url: `/agents/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'Agents', id }],
    }),

    // Agent Tasks
    createAgentTask: builder.mutation<AgentTask, Partial<AgentTask>>({
      query: (task) => ({
        url: '/agents/tasks',
        method: 'POST',
        data: task,
      }),
      invalidatesTags: ['AgentTasks'],
    }),

    getAgentTasks: builder.query<AgentTask[], { project_id?: string; status?: string }>({
      query: (params) => ({
        url: '/agents/tasks',
        method: 'GET',
        params,
      }),
      providesTags: ['AgentTasks'],
    }),

    getAgentTask: builder.query<AgentTask, string>({
      query: (id) => ({
        url: `/agents/tasks/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'AgentTasks', id }],
    }),

    // Code Generation
    generateCode: builder.mutation<CodeGeneration, { prompt: string; language: string; framework?: string }>({
      query: (data) => ({
        url: '/code/generate',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['CodeGenerations'],
    }),

    getCodeGenerations: builder.query<CodeGeneration[], { project_id?: string }>({
      query: (params) => ({
        url: '/code/generations',
        method: 'GET',
        params,
      }),
      providesTags: ['CodeGenerations'],
    }),
    generateProject: builder.mutation({
      query: (data) => ({
        url: '/projects/generate',
        method: 'POST',
        data: {
          name: data.name,
          description: data.description,
          project_type: data.project_type || 'web_app',
          framework: data.framework || 'nextjs',
          features: data.features || [],
          styling: data.styling || 'tailwind',
          custom_requirements: data.custom_requirements || ''
        },
      }),
      invalidatesTags: ['Projects'],
    }),
  }),
});

export const {
  useGetProjectsQuery,
  useLazyGetProjectsQuery,
  useGetProjectQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useGetAgentsQuery,
  useGetAgentQuery,
  useCreateAgentTaskMutation,
  useGetAgentTasksQuery,
  useGetAgentTaskQuery,
  useGenerateCodeMutation,
  useGetCodeGenerationsQuery,
  useGenerateProjectMutation
} = projectsAPI;
